import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Eye } from "lucide-react";
import { toast } from "sonner";
import { AppDispatch, RootState } from "configureStore";
import {
  setDestroy,
  setIsModalxOpen,
  setModalxOptions,
} from "components/modalx/modalx.slice";
import DatePicker from "features/datepicker/date-picker";
import MonthPicker from "features/datepicker/month-picker";
import Currency from "components/format/currency/currency";
import SendStatusBadge from "features/invoice-send/send-status-badge";
import SendInvoiceButton from "features/invoice-send/send-invoice-button";
import SendByEmailCheckbox from "features/invoice-send/send-by-email-checkbox";
import useRememberedSendChoice from "features/invoice-send/use-remembered-send-choice";
import type { InvoiceSendStatus } from "types/Invoice";
import {
  confirmInvoice,
  createFromOrder,
  fetchInvoice,
  patchInvoice,
  previewInvoicePdf,
} from "services/invoices.service";
import { fetchById } from "services/orders.service";
import { fetchOrderDetails } from "containers/order-view-container/order-container.slice";
import { fetchCustomer } from "containers/customer/customer-container.slice";
import { formatInvoicePublicNumber } from "utils/invoice-number";
import {
  formatPeriodMonth,
  fromMonthInput,
  periodEnd,
  toMonthInput,
} from "features/billing-period/billing-period";
import { vilniusYmd } from "utils/vilnius-date";
import {
  invoiceErrorMessage,
  invoiceEmailSkipMessage,
} from "utils/invoice-errors";
import { canConfirmSalesInvoices } from "utils/salesInvoicePermissions";
import {
  inputCls,
  btnLinkCls,
  btnSecondaryCls,
  btnPrimaryCls,
  labelCls,
  helpTextCls,
  errorTextCls,
} from "components/tw/classes";

export type LineRow = {
  id?: string;
  product: string;
  productTitle: string;
  description?: string;
  quantity: number | string;
  price: number | string;
  totalPrice?: number;
  vat?: string;
  externalCode?: string;
};

/**
 * Same rule the create-invoice modal enforces before an order can be
 * invoiced (`invoiceable-order-lines.tsx`'s `lineProblem`): a quantity must
 * be above zero — a 0-quantity line on a built invoice bills nothing — and a
 * price must be zero or more. Re-implemented locally rather than imported:
 * `LineRow` here does not carry the fields `EditedLine` requires.
 *
 * Split by field so each input's `aria-invalid` reflects its own value —
 * a bad price shouldn't flag the quantity field, and vice versa.
 */
function lineQuantityInvalid(line: LineRow): boolean {
  const quantity = line.quantity === "" ? NaN : Number(line.quantity);
  return !Number.isFinite(quantity) || quantity <= 0;
}

function linePriceInvalid(line: LineRow): boolean {
  const price = line.price === "" ? NaN : Number(line.price);
  return !Number.isFinite(price) || price < 0;
}

function lineHasProblem(line: LineRow): boolean {
  return lineQuantityInvalid(line) || linePriceInvalid(line);
}

/**
 * The field labels sit above the first line only, which suits one wide row. A
 * phone wraps the row into a card, so there every line shows its own labels.
 */
function lineLabelCls(index: number): string {
  return index === 0 ? labelCls : `${labelCls} md:hidden`;
}

/**
 * `invoiceitems.quantity`/`price` are DECIMAL(18,6)
 * (14_09_2026_invoice_money_precision.sql); the mysql driver hands them back
 * as strings such as "2.000000" or "3.140000", which a plain `String(...)`
 * showed verbatim in the quantity/price inputs on every draft opened for
 * editing. Trims trailing zeros (and a bare trailing '.') while keeping
 * whatever precision is actually there — "3.140000" -> "3.14",
 * "2.000000" -> "2" — rather than rounding through `Number()`, which would
 * also turn a genuinely empty value into "0".
 */
export function formatDecimalForInput(
  value: number | string | null | undefined
): string {
  if (value == null) return "";
  const str = String(value);
  if (!/^-?\d+(\.\d+)?$/.test(str)) return str; // not a plain decimal — leave it
  if (!str.includes(".")) return str;
  const trimmed = str.replace(/0+$/, "").replace(/\.$/, "");
  return trimmed === "" || trimmed === "-" ? "0" : trimmed;
}

type Props = {
  orderId?: string;
  customerId?: string;
  invoiceId?: string;
  onDone?: () => void;
};


function datePickerToYmd(v: Date | string | null | undefined): string {
  if (!v) return "";
  // Not `v.toISOString().slice(0, 10)`: the picker hands back a plain Date for
  // the day the user clicked, and formatting that through the browser's zone
  // (or naively slicing an ISO string) is exactly what `vilniusYmd` exists to
  // avoid — see utils/vilnius-date.ts.
  return vilniusYmd(v);
}

/**
 * The identity the backend will stamp on the line it builds from this same
 * order item or product-group link — `buildLinePayloadsFromOrder` in
 * erp-backend's `generate-from-order.js` (`erp-order-item-<item.id>` /
 * `erp-order-group-<link.id>`). Sending edits back keyed by this, rather than
 * by array position, is what lets `applyDraftLineEdits` on the backend attach
 * them to the right line even though the draft's own items come back in an
 * unrelated (uuid) order — see the invoice-form line-matching blocker.
 */
function orderItemExternalCode(itemId: string): string {
  return `erp-order-item-${itemId}`;
}
function orderGroupExternalCode(linkId: string): string {
  return `erp-order-group-${linkId}`;
}

export function mapOrderLines(orderEntity: any): LineRow[] {
  const fromItems = (orderEntity?.items || []).map((item: any) => {
    const productId =
      typeof item.product === "object" ? item.product?.id : item.product;
    const qty = Number(item.quantity) || 0;
    const price = Number(item.price ?? item.unitPrice) || 0;
    return {
      id: item.id,
      product: productId,
      productTitle:
        item.productTitle ||
        item.product?.title ||
        item.product?.name ||
        "",
      description: item.description || "",
      quantity: qty,
      price,
      totalPrice:
        item.totalPrice != null ? Number(item.totalPrice) : qty * price,
      vat:
        typeof item.vat === "object" ? item.vat?.id : item.vat || undefined,
      externalCode: item.externalCode || orderItemExternalCode(item.id),
    };
  });
  // One preview row per order/product-group link, in the same order the
  // backend builds them (items first, then groups). These rows are display
  // only: an order line for a product GROUP has no product id of its own —
  // only the backend can resolve the group to the product it invoices — so
  // `product` is left empty and filled in from the created draft on save.
  const fromGroups = (orderEntity?.productGroups || []).map((pg: any) => {
    const group = pg.productGroup || pg;
    const qty = Number(pg.quantity) || 0;
    const price = Number(pg.unitPrice) || 0;
    return {
      product: "",
      productTitle: group?.name || group?.title || "",
      description: pg.description || group?.description || "",
      quantity: qty,
      price,
      totalPrice: pg.totalPrice != null ? Number(pg.totalPrice) : qty * price,
      // `pg.id` is the OrdersToProductGroups LINK row's id (fetch-by-id.js's
      // `orderEntity.productGroups[i].id`) — the same id the backend's
      // `groupLink.id` names, never the product group's own id.
      externalCode: pg.id ? orderGroupExternalCode(pg.id) : undefined,
    };
  });
  return [...fromItems, ...fromGroups];
}

export type InvoiceItemPayload = {
  product: string;
  productTitle: string;
  description?: string;
  quantity: number;
  price: number;
  totalPrice: number;
  vat?: string;
  externalCode?: string;
};

/**
 * The PATCH payload for a draft that ALREADY EXISTS on the server — matches
 * each row on screen to the server's own line by its stable invoice-item
 * `id`, never by array position. The backend returns a draft's items in an
 * unrelated (uuid) order, so an index match is only correct by chance — see
 * the invoice-form line-matching blocker this replaces.
 *
 * A row this cannot match by id falls back to its own product/vat rather than
 * borrowing another line's identity; in practice every row here came FROM the
 * server (`applyInvoice` seeds `lines` from the same list `serverLines`
 * tracks), so the fallback is a safety net, not the common path.
 */
export function buildInvoiceItemsPayload(
  lines: LineRow[],
  serverLines: LineRow[] | null
): InvoiceItemPayload[] {
  const serverById = new Map(
    (serverLines || [])
      .filter((line) => line.id)
      .map((line) => [line.id as string, line])
  );
  return lines.map((line) => {
    const identity = (line.id && serverById.get(line.id)) || line;
    return {
      product: identity.product || line.product,
      productTitle: line.productTitle,
      description: line.description,
      quantity: Number(line.quantity) || 0,
      price: Number(line.price) || 0,
      totalPrice:
        Number(line.totalPrice) ||
        (Number(line.quantity) || 0) * (Number(line.price) || 0),
      vat: identity.vat || line.vat,
      externalCode: identity.externalCode || line.externalCode,
    };
  });
}

export type OrderLineEdit = {
  externalCode: string;
  quantity: number;
  price: number;
  /** Blank means "keep the current title" on the backend — safe to omit. */
  productTitle?: string;
  /** Always sent, even "" — an empty description CLEARS it on the backend. */
  description: string;
};

/**
 * The full current line state to send when creating a draft straight from an
 * order — in the SAME request that creates it (`POST .../orders/:id/invoices`
 * now applies `periodFrom`/`date`/`paymentDueDate`/`lines` inside the create,
 * so no follow-up PATCH is needed for a new draft). Keyed by the externalCode
 * `mapOrderLines` stamps on each preview row (`orderItemExternalCode` /
 * `orderGroupExternalCode`), the same code the backend's
 * `buildLinePayloadsFromOrder` (generate-from-order.js) stamps on the line it
 * builds from that same order item or product-group link — the backend
 * matches these back to it inside the create transaction, and refuses with
 * 409 `ORDER_CHANGED` (creating nothing) when the codes it gets don't match
 * the lines it builds from the CURRENT order. Never by position: the draft's
 * items come back from the server in an unrelated (uuid) order, so an index
 * match agrees with the preview only by chance (1/n! for n lines) — see the
 * invoice-form line-matching blocker.
 *
 * `productTitle`/`description` travel here too — before this, only
 * quantity/price were sent, so renaming a line on a brand-new draft was
 * silently dropped the moment the create response reloaded the form from the
 * server's (unrenamed) items.
 *
 * The backend's `resolveLineEdit` (generate-from-order.js) treats a blank
 * `productTitle` as "keep the current title" (a line must be named) but an
 * EMPTY `description` as "clear it" — so `productTitle` is only worth
 * omitting, never `description`: send it as `""` verbatim, or a user who
 * cleared it would find it silently unchanged.
 *
 * Returns null when a row has no externalCode to key on. That should not
 * happen for a preview `mapOrderLines` built, but if it ever does, silently
 * sending a line with no identity risks the very mismatch this exists to
 * prevent — the caller refuses instead (see the "no reliable key" case in
 * the invoice-form line-matching blocker's write-up).
 */
export function buildOrderLineEdits(lines: LineRow[]): OrderLineEdit[] | null {
  if (!lines.length) return null;
  const edits: OrderLineEdit[] = [];
  for (const line of lines) {
    if (!line.externalCode) return null;
    edits.push({
      externalCode: line.externalCode,
      quantity: Number(line.quantity) || 0,
      price: Number(line.price) || 0,
      productTitle: line.productTitle || undefined,
      description: line.description ?? "",
    });
  }
  return edits;
}

/**
 * Keyed by company+order, shared across every mounted `InvoiceForm` instance
 * for that same order (two browser tabs, a modal reopened while the first
 * create is still in flight, ...). `ownerToken` says which instance's
 * `ensureDraft()` call actually issued the request — see its use in
 * `ensureDraft`.
 */
const createDraftInFlight = new Map<
  string,
  { promise: Promise<any>; ownerToken: string }
>();

function InvoiceForm({
  orderId: orderIdProp,
  customerId,
  invoiceId: initialInvoiceId,
  onDone,
}: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { companyId } = useParams<{ companyId: string }>();
  const { companyEntity } = useSelector(
    (state: RootState) => state.companyContainerReducer
  );
  const { role } = useSelector((state: RootState) => state.authReducer);
  // Same tier `invoices/confirm` requires server-side
  // (SALES_INVOICE_CONFIRM_PERMISSIONS) — a SalesMan can still prepare and
  // save a draft (edit tier), but confirming it needs Master, Admin,
  // Signature or "Access all customers".
  const canConfirm = canConfirmSalesInvoices(role);
  // One id per MOUNT, not per render — lets `ensureDraft` tell whether the
  // pending create it is about to await for this order is one THIS instance
  // issued, or one a different mounted `InvoiceForm` instance issued (both
  // share `createDraftInFlight`, keyed only by company+order).
  const instanceTokenRef = useRef(
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
  );

  // The order this invoice belongs to. Callers pass it; when the form is opened
  // by invoice id alone it is read off the loaded invoice instead. Choosing an
  // order is no longer done here — that is what the Create invoice modal on the
  // customer page is for.
  const [resolvedOrderId, setResolvedOrderId] = useState(orderIdProp || "");
  // The contract number the customer holds; '' when the order has none.
  const [contractRef, setContractRef] = useState("");
  const orderId = orderIdProp || resolvedOrderId;

  const [invoiceId, setInvoiceId] = useState<string | undefined>(
    initialInvoiceId
  );
  const [status, setStatus] = useState<"draft" | "confirmed">("draft");
  const [number, setNumber] = useState<string | null>(null);
  const [publicCode, setPublicCode] = useState<string | null>(null);
  const [alreadyExisted, setAlreadyExisted] = useState(false);
  const settingsSeriesId = companyEntity?.salesInvoiceSeries || "";

  const [date, setDate] = useState(vilniusYmd(new Date()));
  const [paymentDueDate, setPaymentDueDate] = useState(vilniusYmd(new Date()));
  const loadedDateRef = useRef(date);
  const loadedDueRef = useRef(paymentDueDate);
  /**
   * The month a recurring invoice bills (YYYY-MM). Its own field: the date can
   * be anything, and changing it no longer moves the period.
   */
  const [periodMonth, setPeriodMonth] = useState("");
  const loadedPeriodRef = useRef("");
  const [orderIsRecurring, setOrderIsRecurring] = useState(false);
  // Who the invoice is addressed to, as copied onto it when the draft was made.
  const [buyer, setBuyer] = useState<{ name: string; address: string } | null>(
    null
  );
  // Set only for an ERP invoice read from the server.
  const [sendStatus, setSendStatus] = useState<InvoiceSendStatus | null>(null);
  const [sendByEmail, setSendByEmail] = useRememberedSendChoice();
  const [previewing, setPreviewing] = useState(false);
  const [refreshingBuyer, setRefreshingBuyer] = useState(false);

  // The optional service completion act: page 2 of the invoice PDF.
  const [actEnabled, setActEnabled] = useState(false);
  const [actText, setActText] = useState("");
  // The act as the server stores it (null = none), to send only a change.
  const loadedActRef = useRef<string | null>(null);
  const actTextId = useId();

  const [lines, setLines] = useState<LineRow[]>([]);
  // Lines exactly as the backend built them for this invoice. Product identity
  // (product id / VAT / externalCode) is only ever taken from here — the order
  // preview cannot resolve a product group to the product it invoices.
  const serverLinesRef = useRef<LineRow[] | null>(null);
  // Whether the user actually edited a line. An untouched draft must keep the
  // lines the backend built rather than having them overwritten by a preview.
  const linesDirtyRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  const parentsNeedRefresh = useRef(false);

  const refreshParents = () => {
    if (onDone) {
      onDone();
      return;
    }
    if (companyId && orderIdProp) {
      dispatch(fetchOrderDetails({ companyId, orderId: orderIdProp }));
      return;
    }
    if (companyId && customerId) {
      dispatch(fetchCustomer({ companyId, customerId, isEdit: true }) as any);
    }
  };

  const refreshParentsRef = useRef(refreshParents);
  refreshParentsRef.current = refreshParents;

  const closeModal = () => {
    if (parentsNeedRefresh.current) {
      parentsNeedRefresh.current = false;
      refreshParents();
    }
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  useEffect(() => {
    return () => {
      if (parentsNeedRefresh.current) {
        parentsNeedRefresh.current = false;
        refreshParentsRef.current();
      }
    };
  }, []);

  const syncModalTitle = (opts: {
    isDraft: boolean;
    existed: boolean;
    hasId: boolean;
  }) => {
    if (opts.existed || (opts.hasId && !opts.isDraft)) {
      dispatch(
        setModalxOptions({
          title: opts.isDraft
            ? t("invoice_already_exists", {
                defaultValue: "Invoice already exists for this order",
              })
            : t("view_invoice", { defaultValue: "View invoice" }),
        })
      );
    } else if (opts.hasId && opts.isDraft) {
      dispatch(
        setModalxOptions({
          title: t("confirm_invoice", { defaultValue: "Confirm invoice" }),
        })
      );
    } else {
      dispatch(
        setModalxOptions({
          title: t("create_invoice", { defaultValue: "Create invoice" }),
        })
      );
    }
  };

  const totals = useMemo(() => {
    const net = lines.reduce(
      (sum, line) =>
        sum +
        (Number(line.totalPrice) ||
          (Number(line.quantity) || 0) * (Number(line.price) || 0)),
      0
    );
    return { net };
  }, [lines]);

  // Blocks Save and Confirm the same way the create-invoice modal blocks its
  // Draft/Build buttons — a 0 (or cleared) quantity must be fixed or the line
  // removed before this draft can become a legal, numbered document.
  const hasInvalidLine = useMemo(() => lines.some(lineHasProblem), [lines]);

  const trimmedActText = actText.trim();
  // Ticked but nothing written would print an empty act page, so saving waits.
  const serviceActMissingText = actEnabled && trimmedActText === "";
  // What the backend gets: the written text, or null for "no act".
  const serviceActToSend = actEnabled && trimmedActText ? trimmedActText : null;
  const applyBuyer = (invoice: any) => {
    setBuyer({
      name: invoice.customerName || "",
      address: invoice.customerAddress || "",
    });
  };

  const applyInvoice = (invoice: any, existed = false) => {
    const nextStatus =
      invoice.status === "draft"
        ? "draft"
        : invoice.status === "confirmed" || invoice.type !== "erp"
        ? "confirmed"
        : "draft";
    setStatus(nextStatus);
    setNumber(invoice.number ?? null);
    setPublicCode(invoice._number ?? null);
    // The series title is deliberately not tracked: `_number` already carries
    // it, and nothing in the UI shows the series as a separate label.
    setAlreadyExisted(existed);
    const nextDate = invoice.date
      ? vilniusYmd(invoice.date)
      : vilniusYmd(new Date());
    const nextDue = invoice.paymentDueDate
      ? vilniusYmd(invoice.paymentDueDate)
      : nextDate;
    setDate(nextDate);
    setPaymentDueDate(nextDue);
    loadedDateRef.current = nextDate;
    loadedDueRef.current = nextDue;
    const nextPeriod = toMonthInput(invoice.periodFrom);
    setPeriodMonth(nextPeriod);
    loadedPeriodRef.current = nextPeriod;
    // Only `invoices/get` says whether the order is recurring; create and
    // patch responses leave it as it was. An invoice carrying a period does
    // not imply it: a one-off order's invoice may carry one too.
    if (typeof invoice.orderIsRecurring === "boolean") {
      setOrderIsRecurring(invoice.orderIsRecurring);
    }
    // Every invoice response (get, create, patch, confirm) is the whole stored
    // record, so the act on screen and the one compared on save always follow
    // what the server holds.
    const nextAct: string = invoice.serviceActText || "";
    setActEnabled(nextAct !== "");
    setActText(nextAct);
    loadedActRef.current = nextAct || null;
    applyBuyer(invoice);
    setSendStatus(invoice.type === "erp" ? invoice.send || null : null);
    const serverLines: LineRow[] = (invoice.items || []).map((item: any) => ({
      id: item.id,
      product:
        typeof item.product === "object" ? item.product?.id : item.product,
      productTitle: item.productTitle || "",
      description: item.description || "",
      quantity: formatDecimalForInput(item.quantity),
      price: formatDecimalForInput(item.price),
      totalPrice: item.totalPrice,
      vat: typeof item.vat === "object" ? item.vat?.id : item.vat,
      externalCode: item.externalCode,
    }));
    serverLinesRef.current = serverLines;
    linesDirtyRef.current = false;
    setLines(serverLines);
    syncModalTitle({
      isDraft: nextStatus === "draft",
      existed,
      hasId: true,
    });
  };

  // Load existing invoice OR preview order lines — never POST a draft on mount.
  useEffect(() => {
    if (!companyId) return;
    if (!initialInvoiceId && !orderId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErrMsg("");
      try {
        if (initialInvoiceId) {
          const result = await fetchInvoice({
            companyId,
            invoiceId: initialInvoiceId,
          });
          if (cancelled) return;
          const invoice = result.invoice ?? result;
          setInvoiceId(invoice.id);
          if (invoice.order) {
            const orderIdValue =
              typeof invoice.order === "object"
                ? invoice.order.id
                : invoice.order;
            if (orderIdValue) setResolvedOrderId(orderIdValue);
          }
          setContractRef(invoice.contractRef || "");
          applyInvoice(invoice, false);
        } else {
          const { orderEntity } = await fetchById(orderId, companyId);
          if (cancelled) return;
          setContractRef(
            orderEntity?._number || orderEntity?.externalNumber || ""
          );
          const mapped = mapOrderLines(orderEntity);
          // Preview only — the draft does not exist yet, so there is no
          // server-built line to take product identity from.
          serverLinesRef.current = null;
          linesDirtyRef.current = false;
          setLines(mapped);
          const today = vilniusYmd(new Date());
          setDate(today);
          const delay = Number(orderEntity?.customer?.paymentDelay) || 0;
          const due = new Date();
          due.setDate(due.getDate() + delay);
          const dueYmd = vilniusYmd(due);
          setPaymentDueDate(dueYmd);
          loadedDateRef.current = today;
          loadedDueRef.current = dueYmd;
          // The month a new invoice of a recurring order bills is the server's
          // `invoiceBilling.nextPeriod` — the same period draft creation will
          // take. Showing it here lets the user see and change it before the
          // first Save/Confirm; recording it as "loaded" means an untouched
          // picker sends no period patch after the draft is created.
          const recurring = !!orderEntity?.isRecurring;
          setOrderIsRecurring(recurring);
          const nextPeriod = recurring
            ? toMonthInput(orderEntity?.invoiceBilling?.nextPeriod?.periodFrom)
            : "";
          setPeriodMonth(nextPeriod);
          loadedPeriodRef.current = nextPeriod;
          syncModalTitle({ isDraft: true, existed: false, hasId: false });
        }
      } catch (e: any) {
        if (!cancelled) {
          setErrMsg(
            e?.response?.data?.message ||
              e?.message ||
              t("error", { defaultValue: "Error" })
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, orderId, customerId, initialInvoiceId]);

  const updateLine = (index: number, patch: Partial<LineRow>) => {
    linesDirtyRef.current = true;
    setLines((prev) =>
      prev.map((line, i) => {
        if (i !== index) return line;
        const next = { ...line, ...patch };
        const qty = Number(next.quantity) || 0;
        const price = Number(next.price) || 0;
        next.totalPrice = qty * price;
        return next;
      })
    );
  };

  /**
   * The server's reason, in the user's language where we have one —
   * `PERIOD_ALREADY_BILLED`, `INVOICE_DATE_BEFORE_LAST_ISSUED` and the other
   * codes these invoice endpoints answer with, one place shared with the
   * drafts panel and the create-invoice modal (utils/invoice-errors.ts).
   */
  const errorMessage = (e: any): string => invoiceErrorMessage(e, t);

  const ensureDraft = async (): Promise<{
    id: string;
    invoice: any;
    alreadyExists: boolean;
  }> => {
    if (invoiceId) {
      return {
        id: invoiceId,
        invoice: {
          id: invoiceId,
          status,
          number,
        },
        alreadyExists: alreadyExisted,
      };
    }
    if (!companyId || !orderId) {
      throw new Error(t("error", { defaultValue: "Error" }));
    }
    // One creation path. The order already determines its customer, so there is
    // nothing a customer-scoped variant of this call could add.
    const key = `${companyId}:${orderId}`;
    let entry = createDraftInFlight.get(key);
    // No entry yet: THIS call is the one issuing the request (nothing runs
    // between the `.get` above and the `.set` below, so no other call can
    // interleave and also see no entry for the same key).
    const isOwnRequest = !entry;
    if (!entry) {
      const orderLineEdits = buildOrderLineEdits(lines);
      if (lines.length > 0 && !orderLineEdits) {
        // Every preview row `mapOrderLines` builds carries an externalCode; if
        // one is somehow missing there is no safe key to send that edit
        // under. Refuse rather than create a draft with no reliable identity
        // for a line — see the invoice-form line-matching blocker.
        throw new Error(
          t("invoice_lines_out_of_sync", {
            defaultValue:
              "This invoice changed elsewhere. Close and reopen it before editing the lines.",
          })
        );
      }
      // Blank for a one-off invoice with no period chosen, so nothing is sent.
      const periodFrom = periodMonth ? fromMonthInput(periodMonth) : undefined;
      const promise = createFromOrder({
        companyId,
        orderId,
        data: {
          seriesId: settingsSeriesId || undefined,
          date,
          paymentDueDate,
          ...(periodFrom ? { periodFrom } : {}),
          // A new draft starts with no act, so only a written one is sent.
          ...(serviceActToSend ? { serviceActText: serviceActToSend } : {}),
          // Sent in the SAME request that creates the draft, and applied
          // inside it — the backend matches each line to the one it builds
          // from the same order item/product-group by externalCode, and
          // creates nothing (409 `ORDER_CHANGED`) if those codes don't match
          // the lines it builds from the CURRENT order. No follow-up PATCH:
          // matching a separately-fetched draft's items by array position
          // against whatever order the server happened to return them in is
          // what caused the line-matching blocker this replaces.
          ...(orderLineEdits ? { lines: orderLineEdits } : {}),
        },
      })
        .catch(async (e: any) => {
          const code = e?.response?.data?.code;
          const existingId = e?.response?.data?.invoiceId;
          // The order already had a draft when we tried to create one —
          // orders/create-invoice.js refuses the whole request rather than
          // silently overwriting someone else's work in progress (code
          // ALREADY_EXISTS, with the existing invoice's id). Load it instead
          // of surfacing a raw 400.
          if (code === "ALREADY_EXISTS" && existingId) {
            const existing = await fetchInvoice({
              companyId,
              invoiceId: existingId,
            });
            return {
              invoice: existing.invoice ?? existing,
              created: false,
              alreadyExists: true,
            };
          }
          // The order's lines changed after this preview was loaded (someone
          // else edited it) — the backend refused rather than silently
          // building a draft from a mismatch between what the user saw and
          // what the order now says. Reload the order's current lines so the
          // user edits against what would actually be invoiced; their
          // date/due-date/period choices are left exactly as they are.
          if (code === "ORDER_CHANGED") {
            try {
              const { orderEntity } = await fetchById(orderId, companyId);
              serverLinesRef.current = null;
              linesDirtyRef.current = false;
              setLines(mapOrderLines(orderEntity));
            } catch {
              // Best effort — the message below still tells the user to retry.
            }
            throw new Error(
              t("invoice_order_changed_reloaded", {
                defaultValue:
                  "The order changed while this form was open. The lines were reloaded — check them and try again.",
              })
            );
          }
          throw e;
        })
        .finally(() => {
          createDraftInFlight.delete(key);
        });
      entry = { promise, ownerToken: instanceTokenRef.current };
      createDraftInFlight.set(key, entry);
    }
    const result = await entry.promise;
    // A pending create THIS instance issued reports `alreadyExists` as the
    // server (or the ALREADY_EXISTS fallback above) determined it. A pending
    // create a DIFFERENT mounted InvoiceForm instance issued produced a draft
    // this instance never sent its own date/period/line edits to — treat it
    // exactly like ALREADY_EXISTS so the caller loads and shows it, rather
    // than silently confirming or editing someone else's in-flight result
    // with edits that were never actually applied to it.
    const existed = isOwnRequest ? !!result.alreadyExists : true;
    setInvoiceId(result.invoice.id);
    applyInvoice(result.invoice, existed);
    parentsNeedRefresh.current = true;
    if (existed) {
      toast.info(
        t("invoice_already_exists", {
          defaultValue: "Invoice already exists for this order",
        })
      );
    }
    return {
      id: result.invoice.id as string,
      invoice: result.invoice,
      alreadyExists: existed,
    };
  };

  /**
   * Send only what the user actually changed. `sendItems` is captured before
   * the draft is created, because creating one reloads the lines from the
   * server and would otherwise clear the dirty flag we are testing.
   */
  const patchDirtyFields = async (
    id: string,
    existed: boolean,
    sendItems: boolean,
    dateChanged: boolean,
    dueChanged: boolean,
    periodChanged: boolean,
    actChanged: boolean
  ) => {
    const data: Record<string, unknown> = {};
    if (sendItems) {
      const items = buildInvoiceItemsPayload(lines, serverLinesRef.current);
      // A product-group preview row carries no product id of its own; it only
      // gets one from the draft the backend built. If the two ever disagree in
      // shape we cannot say which product a row means — say so plainly instead
      // of posting a line with an empty product and surfacing a raw 400.
      if (items.some((line) => !line.product)) {
        throw new Error(
          t("invoice_lines_out_of_sync", {
            defaultValue:
              "This invoice changed elsewhere. Close and reopen it before editing the lines.",
          })
        );
      }
      data.items = items;
    }
    if (dateChanged) data.date = date;
    if (dueChanged) data.paymentDueDate = paymentDueDate;
    if (periodChanged) {
      // `null` clears the month of a one-off invoice.
      const periodFrom = fromMonthInput(periodMonth);
      data.periodFrom = periodFrom;
      // A recurring invoice always bills one whole month and the server
      // refuses anything else; a one-off invoice's end is set by the server.
      if (periodFrom && orderIsRecurring) data.periodTo = periodEnd(periodFrom);
    }
    if (actChanged) data.serviceActText = serviceActToSend;
    if (!Object.keys(data).length) return;
    const { invoice } = await patchInvoice({
      companyId: companyId!,
      invoiceId: id,
      data,
    });
    applyInvoice(invoice, existed);
  };

  const handleSaveDraft = async () => {
    if (!companyId || status !== "draft") return;
    setSaving(true);
    setErrMsg("");
    try {
      const hadInvoiceId = !!invoiceId;
      const linesEdited = linesDirtyRef.current;
      const dateChanged = date !== loadedDateRef.current;
      const dueChanged = paymentDueDate !== loadedDueRef.current;
      const periodChanged = periodMonth !== loadedPeriodRef.current;
      const actChanged = serviceActToSend !== loadedActRef.current;
      const { id, invoice, alreadyExists } = await ensureDraft();
      // The order already had a draft — someone else's, or the cron's. The
      // edits on screen were made to a preview of the ORDER, not to that draft,
      // so writing them over it would silently replace work nobody reviewed.
      // `ensureDraft` has loaded the existing draft into the form instead; the
      // user sees it and can edit and save that one. Confirm stops the same way.
      if (alreadyExists && !hadInvoiceId) {
        setErrMsg(
          t("invoice_existing_draft_loaded", {
            defaultValue:
              "This order already has a draft invoice, so your changes were not applied to it. It is now shown here — review it, then edit and save.",
          })
        );
        return;
      }
      if (invoice.status !== "draft" && invoice.type === "erp") {
        return;
      }
      // A freshly created draft already carries the user's date/period/lines
      // from the create request itself (see `ensureDraft`) — nothing left to
      // patch. Only an EXISTING draft being edited in place still needs one.
      if (hadInvoiceId && (invoice.status === "draft" || !invoice.status)) {
        await patchDirtyFields(
          id,
          alreadyExists,
          linesEdited,
          dateChanged,
          dueChanged,
          periodChanged,
          actChanged
        );
      }
      parentsNeedRefresh.current = false;
      refreshParents();
      toast.success(t("saved_successfully", { defaultValue: "Saved" }));
    } catch (e: any) {
      setErrMsg(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const handleConfirm = async () => {
    if (!companyId) return;
    setConfirming(true);
    setErrMsg("");
    try {
      const hadInvoiceId = !!invoiceId;
      const linesEdited = linesDirtyRef.current;
      const dateChanged = date !== loadedDateRef.current;
      const dueChanged = paymentDueDate !== loadedDueRef.current;
      const periodChanged = periodMonth !== loadedPeriodRef.current;
      const actChanged = serviceActToSend !== loadedActRef.current;
      const { id, invoice: ensured, alreadyExists } = await ensureDraft();
      if (alreadyExists && !hadInvoiceId) {
        setErrMsg(
          t("invoice_existing_draft_loaded", {
            defaultValue:
              "This order already has a draft invoice, so your changes were not applied to it. It is now shown here — review it, then edit and save.",
          })
        );
        return;
      }
      const stillDraft =
        ensured.status === "draft" ||
        (ensured.type === "erp" && !ensured.number && ensured.status !== "confirmed");
      // See handleSaveDraft: a freshly created draft's date/period/lines
      // already travelled with the create request, so only an EXISTING draft
      // being edited in place still needs a patch here.
      if (hadInvoiceId && stillDraft) {
        await patchDirtyFields(
          id,
          alreadyExists,
          linesEdited,
          dateChanged,
          dueChanged,
          periodChanged,
          actChanged
        );
      }
      const result = await confirmInvoice({
        companyId,
        invoiceId: id,
        send: sendByEmail,
      });
      applyInvoice(result.invoice, alreadyExists);
      // Shared with the drafts panel (utils/invoice-errors.ts) so the same
      // emailSkipReason always reads the same way, whether confirmed here or
      // from "Confirm all".
      const mapped = invoiceEmailSkipMessage(
        t,
        result.emailSkipReason,
        !!result.emailSent,
        result.emailError
      );
      if (mapped.ok) toast.success(mapped.message);
      else toast.warning(mapped.message);
      parentsNeedRefresh.current = false;
      refreshParents();
      dispatch(setIsModalxOpen(false));
      dispatch(setDestroy());
    } catch (e: any) {
      setErrMsg(errorMessage(e));
    } finally {
      setConfirming(false);
    }
  };

  const handlePreview = async () => {
    if (!companyId || !invoiceId) return;
    setPreviewing(true);
    setErrMsg("");
    try {
      await previewInvoicePdf({ companyId, invoiceId });
    } catch (e: any) {
      setErrMsg(errorMessage(e));
    } finally {
      setPreviewing(false);
    }
  };

  const handleRefreshBuyer = async () => {
    if (!companyId || !invoiceId) return;
    setRefreshingBuyer(true);
    setErrMsg("");
    try {
      const { invoice } = await patchInvoice({
        companyId,
        invoiceId,
        data: { refreshBuyer: true },
      });
      // Only the buyer: the rest of the form may hold edits not saved yet.
      applyBuyer(invoice);
      parentsNeedRefresh.current = true;
      toast.success(
        t("invoice_buyer_refreshed", { defaultValue: "Buyer details updated" })
      );
    } catch (e: any) {
      setErrMsg(errorMessage(e));
    } finally {
      setRefreshingBuyer(false);
    }
  };

  if (loading) {
    return (
      <p className="text-sm text-zinc-500">
        {t("loading", { defaultValue: "Loading…" })}
      </p>
    );
  }

  const isDraft = status === "draft";
  // The preview renders what is SAVED, so it is offered only while the form
  // holds nothing newer than that.
  const hasUnsavedChanges =
    linesDirtyRef.current ||
    date !== loadedDateRef.current ||
    paymentDueDate !== loadedDueRef.current ||
    periodMonth !== loadedPeriodRef.current ||
    serviceActToSend !== loadedActRef.current;

  return (
    <div className="space-y-4">
      {errMsg && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {errMsg}
          <button
            type="button"
            className="ml-2 text-xs underline"
            onClick={() => setErrMsg("")}
          >
            {t("close")}
          </button>
        </div>
      )}
      {alreadyExisted && isDraft && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700/40 dark:bg-amber-500/10 dark:text-amber-200">
          {t("invoice_already_exists", {
            defaultValue: "Invoice already exists for this order",
          })}
        </div>
      )}
      {!isDraft && (
        <div className="rounded-lg border border-green-500/30 bg-green-500/10 px-3 py-2 text-sm text-green-800 dark:text-green-300">
          {t("invoice_confirmed_readonly", {
            defaultValue:
              "This invoice is confirmed and can no longer be edited.",
          })}
        </div>
      )}
      {!isDraft && sendStatus && invoiceId && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <SendStatusBadge send={sendStatus} showDetails />
          {/* Sending needs the same permission tier as confirming. */}
          {canConfirm && (
            <SendInvoiceButton
              invoice={{
                id: invoiceId,
                _number: publicCode,
                number,
                status,
                send: sendStatus,
              }}
              onSent={(send) => {
                setSendStatus(send);
                parentsNeedRefresh.current = true;
              }}
            />
          )}
        </div>
      )}

      <div className="space-y-1 text-sm text-zinc-700 dark:text-zinc-200">
        <p>
          {t("invoice_number", { defaultValue: "Invoice number" })}:{" "}
          <span className="font-medium">
            {formatInvoicePublicNumber({
              _number: publicCode,
              number,
              status,
            }) || t("draft", { defaultValue: "Draft" })}
          </span>
        </p>
        {contractRef && (
          <p>
            {t("invoice_contract", { defaultValue: "Contract" })}:{" "}
            <span className="font-medium">{contractRef}</span>
          </p>
        )}
        {buyer && (
          <p>
            {t("invoice_buyer", { defaultValue: "Buyer" })}:{" "}
            <span className="font-medium">
              {[buyer.name, buyer.address].filter(Boolean).join(", ") || "—"}
            </span>
            {/* The buyer is copied onto the draft when it is made, so a
                customer renamed since then needs this to reach the invoice. */}
            {isDraft && (
              <button
                type="button"
                className={`${btnLinkCls} ml-2 !text-xs`}
                disabled={refreshingBuyer}
                onClick={handleRefreshBuyer}
              >
                {t("invoice_refresh_buyer", {
                  defaultValue: "Update buyer details",
                })}
              </button>
            )}
          </p>
        )}
      </div>

      <div className="flex flex-wrap gap-4">
        <div className="min-w-[180px]">
          <DatePicker
            title={t("date")}
            selected={date}
            onChange={(v: Date | string | null) =>
              setDate(datePickerToYmd(v))
            }
            readOnly={!isDraft}
            disableDatesAfter={null}
          />
        </div>
        <div className="min-w-[180px]">
          <DatePicker
            title={t("payment_due_date")}
            selected={paymentDueDate}
            onChange={(v: Date | string | null) =>
              setPaymentDueDate(datePickerToYmd(v))
            }
            readOnly={!isDraft}
            disableDatesAfter={null}
          />
        </div>
        {/* The billed month: required on a recurring order's draft, optional on
            a one-off order's (clearing it prints no period), read-only once
            confirmed. */}
        {periodMonth && isDraft && orderIsRecurring ? (
          <div className="min-w-[180px]">
            <MonthPicker
              label={t("invoice_period")}
              value={periodMonth}
              isRequired
              onChange={setPeriodMonth}
            />
            <p className="mt-1 max-w-xs text-xs text-zinc-500">
              {t("invoice_period_help")}
            </p>
          </div>
        ) : isDraft && !orderIsRecurring ? (
          <div className="min-w-[180px]">
            <MonthPicker
              label={t("invoice_period")}
              value={periodMonth}
              onChange={setPeriodMonth}
            />
            <p className="mt-1 max-w-xs text-xs text-zinc-500">
              {t("invoice_period_one_off_help", {
                defaultValue:
                  "Optional. Pick the month this invoice bills; it is printed on the invoice.",
              })}
            </p>
          </div>
        ) : periodMonth ? (
          <div className="min-w-[180px] text-sm">
            <span className={labelCls}>{t("invoice_period")}</span>
            <span className="font-medium text-zinc-800 dark:text-zinc-100">
              {formatPeriodMonth(`${periodMonth}-01`)}
            </span>
          </div>
        ) : null}
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">
          {t("products")}
        </h3>
        {lines.map((line, index) => (
          // On a phone the row is a card of its own: name on top, quantity and
          // price below, the sum under them.
          <div
            key={line.id || index}
            className="flex flex-wrap items-end gap-2 max-md:grid max-md:grid-cols-2 max-md:rounded-lg max-md:border max-md:border-zinc-200 max-md:p-3 dark:max-md:border-zinc-700"
          >
            <div className="min-w-[160px] flex-[2] max-md:col-span-2">
              <label className={lineLabelCls(index)}>{t("product_name")}</label>
              <input
                className={inputCls}
                value={line.productTitle}
                onChange={(e) =>
                  updateLine(index, { productTitle: e.target.value })
                }
                disabled={!isDraft}
              />
            </div>
            <div className="w-[90px] max-md:w-auto">
              <label className={lineLabelCls(index)}>{t("quantity")}</label>
              <input
                className={inputCls}
                type="number"
                value={String(line.quantity)}
                onChange={(e) =>
                  updateLine(index, { quantity: e.target.value })
                }
                disabled={!isDraft}
                aria-invalid={lineQuantityInvalid(line)}
              />
            </div>
            <div className="w-[110px] max-md:w-auto">
              <label className={lineLabelCls(index)}>{t("price")}</label>
              <input
                className={inputCls}
                type="number"
                value={String(line.price)}
                onChange={(e) => updateLine(index, { price: e.target.value })}
                disabled={!isDraft}
                aria-invalid={linePriceInvalid(line)}
              />
            </div>
            <div className="w-[110px] pb-2 text-sm max-md:col-span-2 max-md:w-auto max-md:pb-0 max-md:text-right">
              <Currency
                value={
                  Number(line.totalPrice) ||
                  (Number(line.quantity) || 0) * (Number(line.price) || 0)
                }
                isSymbol
              />
            </div>
          </div>
        ))}
        {hasInvalidLine && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-700 dark:text-red-300">
            {t("invoice_form_line_invalid", {
              defaultValue:
                "A product line has no quantity, or a negative price. Fix it before saving or confirming this invoice.",
            })}
          </div>
        )}
        <p className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">
          {t("totalWithoutVat", { defaultValue: "Total without VAT" })}:{" "}
          <Currency value={totals.net} isSymbol />
        </p>
      </div>

      {isDraft ? (
        <div className="space-y-2">
          <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-zinc-800 dark:text-zinc-100">
            <input
              type="checkbox"
              checked={actEnabled}
              onChange={(e) => setActEnabled(e.target.checked)}
              disabled={saving || confirming}
              className="h-4 w-4 rounded border-zinc-300 text-indigo-600 focus:ring-indigo-500"
            />
            {t("invoice_service_act_add", {
              defaultValue: "Attach a service completion act",
            })}
          </label>
          {actEnabled && (
            <div>
              <label htmlFor={actTextId} className={labelCls}>
                {t("invoice_service_act_text", { defaultValue: "Work done" })}
              </label>
              <textarea
                id={actTextId}
                className={inputCls}
                rows={6}
                value={actText}
                onChange={(e) => setActText(e.target.value)}
                disabled={saving || confirming}
                aria-invalid={serviceActMissingText}
              />
              <p className={serviceActMissingText ? errorTextCls : helpTextCls}>
                {serviceActMissingText
                  ? t("invoice_service_act_required", {
                      defaultValue:
                        "Enter the work done, or untick the service completion act.",
                    })
                  : t("invoice_service_act_help", {
                      defaultValue:
                        "Printed as the second page of the invoice PDF — same file, same e-mail.",
                    })}
              </p>
            </div>
          )}
        </div>
      ) : (
        actText && (
          <div>
            <span className={labelCls}>
              {t("invoice_service_act_title", {
                defaultValue: "Service completion act",
              })}
            </span>
            <p className="whitespace-pre-wrap text-sm text-zinc-800 dark:text-zinc-100">
              {actText}
            </p>
          </div>
        )
      )}

      <div className="flex flex-wrap items-center justify-end gap-3">
        <div className="mr-auto flex flex-wrap items-center gap-2">
          {isDraft && invoiceId && (
            <>
              <button
                type="button"
                className={btnSecondaryCls}
                disabled={previewing || hasUnsavedChanges}
                onClick={handlePreview}
              >
                <Eye className="size-4" aria-hidden="true" />
                {t("invoice_preview_pdf", { defaultValue: "Preview PDF" })}
              </button>
              {hasUnsavedChanges && (
                <span className="text-xs text-zinc-500">
                  {t("invoice_preview_save_first", {
                    defaultValue: "Save to preview your changes",
                  })}
                </span>
              )}
            </>
          )}
        </div>
        <button type="button" className={btnSecondaryCls} onClick={closeModal}>
          {t("close")}
        </button>
        {isDraft && (
          <>
            <button
              type="button"
              className={btnSecondaryCls}
              disabled={
                saving || confirming || hasInvalidLine || serviceActMissingText
              }
              onClick={handleSaveDraft}
            >
              {saving
                ? t("loading", { defaultValue: "Loading…" })
                : t("save")}
            </button>
            {/* Confirming assigns the legal number and renders the PDF (and
                emails it when ticked) — the backend requires the confirm tier
                (SALES_INVOICE_CONFIRM_PERMISSIONS) for invoices/confirm and
                answers 403 for anyone else. Hidden rather than shown-then-403'd,
                the same choice the create-invoice modal makes for Build. */}
            {canConfirm ? (
              <>
                <SendByEmailCheckbox
                  checked={sendByEmail}
                  onChange={setSendByEmail}
                  disabled={confirming}
                />
                <button
                  type="button"
                  className={btnPrimaryCls}
                  disabled={
                    confirming ||
                    saving ||
                    !lines.length ||
                    hasInvalidLine ||
                    serviceActMissingText
                  }
                  onClick={handleConfirm}
                >
                  {confirming
                    ? t("loading", { defaultValue: "Loading…" })
                    : t("confirm_invoice", { defaultValue: "Confirm invoice" })}
                </button>
              </>
            ) : (
              <span className="self-center text-xs text-zinc-500">
                {t("invoice_confirm_needs_permission", {
                  defaultValue:
                    "Confirming needs the Master, Admin, Signature or “Access all customers” permission. Save keeps it as a draft.",
                })}
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default InvoiceForm;
